import { keepPreviousData, useQuery } from '@tanstack/react-query';
import client from '../api/client';
import { USER_TYPE } from '../helper/constant/userType';

export function computeLocationKey(locationFilters) {
  const temparr = [];
  if (locationFilters['กรุงเทพมหานคร']) temparr.push('กรุงเทพมหานคร');
  if (locationFilters['สมุทรปราการ']) temparr.push('สมุทรปราการ');
  if (locationFilters['นนทบุรี']) temparr.push('นนทบุรี');
  if (locationFilters['นครปฐม']) temparr.push('นครปฐม');
  if (locationFilters['ปทุมธานี']) temparr.push('ปทุมธานี');
  return temparr.length > 0 ? temparr.join(',') : 'none';
}

function appendLocationFilter(formdata, locationFilters) {
  const temparr = [];
  if (locationFilters['กรุงเทพมหานคร']) temparr.push('กรุงเทพมหานคร');
  if (locationFilters['สมุทรปราการ']) temparr.push('สมุทรปราการ');
  if (locationFilters['นนทบุรี']) temparr.push('นนทบุรี');
  if (locationFilters['นครปฐม']) temparr.push('นครปฐม');
  if (locationFilters['ปทุมธานี']) temparr.push('ปทุมธานี');

  if (temparr.length > 0) {
    formdata.append('location_enable', 'enable');
    formdata.append('location_filter', temparr.join(','));
  }
}

const FORM_HEADERS = {
  Accept: 'application/json',
};

function assertOk(response) {
  if (response.data.status !== 'OK') {
    const message = response.data.message || 'request failed';
    const detail = response.data.error;
    throw new Error(detail ? `${message} (${detail})` : message);
  }
  return response.data;
}

/**
 * Market item-list queries for MarketPlace.js — one useQuery per endpoint, enabled by active view.
 */
export function useMarketPlaceQueries({
  permitdata,
  buyerCategory,
  viewCategory,
  searchType,
  productId,
  searchText,
  productCategoryId,
  locationKey,
  locationFilters,
  promotionPage,
}) {
  const canBrowseMarket = Boolean(
    permitdata?.user_type === USER_TYPE.ALL || permitdata?.user_type === USER_TYPE.BUYER,
  );

  const canBrowseCategory = Boolean(
    canBrowseMarket && productCategoryId && buyerCategory?.includes(productCategoryId),
  );

  const isHome = Boolean(viewCategory === 'รวมสินค้า');
  const isPromotion = Boolean(viewCategory === 'สินค้าโปรโมชั่น');
  const isAdsView = Boolean(viewCategory === 'สินค้าแนะนำ' || viewCategory === 'เพื่อโฆษณา');
  const isSearch = Boolean(viewCategory === 'ค้นหาสินค้า');
  const isSearchContain = Boolean(isSearch && searchType === 'contain');
  const isSearchProductId = Boolean(isSearch && searchType === 'productid');
  const isCategoryView = Boolean(
    viewCategory &&
      viewCategory !== '-' &&
      !isHome &&
      !isPromotion &&
      !isAdsView &&
      !isSearch &&
      !!productCategoryId,
  );

  const multirandomQuery = useQuery({
    queryKey: ['market', 'multirandom', locationKey],
    queryFn: async () => {
      const formdata = new FormData();
      appendLocationFilter(formdata, locationFilters);
      const response = await client.post('/api/market/itemlist/multirandom', formdata, {
        headers: FORM_HEADERS,
      });
      const data = assertOk(response);
      return {
        items_pro: data.items_pro ?? [],
        items_best: data.items_best ?? [],
      };
    },
    enabled: isHome,
  });

  const categoryQuery = useQuery({
    queryKey: ['market', 'category', productCategoryId, locationKey],
    queryFn: async () => {
      const formdata = new FormData();
      formdata.append('productcategory_id', productCategoryId);
      appendLocationFilter(formdata, locationFilters);
      const response = await client.post('/api/market/itemlist/category', formdata, {
        headers: FORM_HEADERS,
      });
      const data = assertOk(response);
      return { items: data.items ?? [] };
    },
    enabled: Boolean(isCategoryView && canBrowseCategory),
  });

  const promotionQuery = useQuery({
    queryKey: ['market', 'promotion', promotionPage, locationKey],
    queryFn: async () => {
      const formdata = new FormData();
      appendLocationFilter(formdata, locationFilters);
      formdata.append('page', String(promotionPage));
      const response = await client.post('/api/market/itemlist/promotion', formdata, {
        headers: FORM_HEADERS,
      });
      const data = assertOk(response);
      return { items: data.items ?? [], total: data.total ?? 0 };
    },
    enabled: Boolean(isPromotion && canBrowseMarket),
    placeholderData: keepPreviousData,
  });

  const adsQuery = useQuery({
    queryKey: ['market', 'ads', locationKey],
    queryFn: async () => {
      const formdata = new FormData();
      appendLocationFilter(formdata, locationFilters);
      const response = await client.post('/api/market/itemlist/ads', formdata, {
        headers: FORM_HEADERS,
      });
      const data = assertOk(response);
      return { items: data.items ?? [] };
    },
    enabled: Boolean(isAdsView && canBrowseMarket),
  });

  const searchContainQuery = useQuery({
    queryKey: ['market', 'search-contain', searchText, locationKey],
    queryFn: async () => {
      const formdata = new FormData();
      formdata.append('findtext', searchText);
      appendLocationFilter(formdata, locationFilters);
      const response = await client.post('/api/market/itemlist/search/productcontain', formdata, {
        headers: FORM_HEADERS,
      });
      const data = assertOk(response);
      return { items: data.items ?? [] };
    },
    enabled: Boolean(isSearchContain && !!searchText),
  });

  const searchProductIdQuery = useQuery({
    queryKey: ['market', 'search-productid', productId, locationKey],
    queryFn: async () => {
      const formdata = new FormData();
      formdata.append('productitem_id', productId);
      appendLocationFilter(formdata, locationFilters);
      const response = await client.post('/api/market/itemlist/search/productid', formdata, {
        headers: FORM_HEADERS,
      });
      const data = assertOk(response);
      return { items: data.items ?? [] };
    },
    enabled: Boolean(isSearchProductId && !!productId),
  });

  const activeQuery = isHome
    ? multirandomQuery
    : isPromotion
      ? promotionQuery
      : isAdsView
        ? adsQuery
        : isSearchContain
          ? searchContainQuery
          : isSearchProductId
            ? searchProductIdQuery
            : isCategoryView
              ? categoryQuery
              : null;

  return {
    multirandomQuery,
    categoryQuery,
    promotionQuery,
    adsQuery,
    searchContainQuery,
    searchProductIdQuery,
    activeQuery,
    isHome,
    isPromotion,
    isAdsView,
    isSearch,
    isCategoryView,
    canBrowseCategory,
  };
}
